import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { RenderIf } from 'react-rainbow-components';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import Languages from 'components/icons/languages';
import Close from 'components/icons/close';
import Gradient from 'components/Gradient';
import useSearchFilters from 'hooks/useSearchFilters';
import Calendar from 'components/icons/calendar';
import Accordion from '../../../Opportunities/View/accordion';
import {
    FilterHeader,
    Title,
    FiltersContainer,
    FiltersContent,
} from '../../../Opportunities/View/styled';
import {
    TagIcon, StyledDatePickerFilter, StyledEntityTagsFilter,
    StyledLanguageFilter,
} from './styled';

interface Params {
    filterNames?: string[];
    agentId: string;
    onRequestClose?: () => void;
}

const Filters = ({
    agentId,
    filterNames = [],
    onRequestClose = () => {},
}: Params) => {
    const {
        values,
        getValue: getFilterValue,
        setValue: setFilterValue,
        clear: clearFilters,
    } = useSearchFilters({ filterNames });

    const activeFiltersCount = useMemo(
        () => filterNames.reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [getFilterValue, filterNames],
    );

    const createdAtFilterValue = useMemo(
        () => {
            const { createdAt } = values;
            if (!createdAt) return undefined;
            return String(createdAt).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    const handleChangeCreatedAt = useCallback(
        (newFilterValue: Date[]) => setFilterValue(
            'createdAt',
            (
                newFilterValue.length === 0
                    ? null
                    : newFilterValue.map((date) => date.toISOString()).join(',')
            ),
        ),
        [setFilterValue],
    );

    const tagsFilterValue = values.tags !== null ? String(values.tags).split(',') : null;

    const handleChangeTagsFilter = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'tags',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    const languageFilterValue = values.language !== null ? String(values.language).split(',') : null;

    const handleChangeLanguageFilter = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'language',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    return (
        <FiltersContainer>
            <FilterHeader>
                <Title>
                    Filters
                    {' '}
                    <RenderIf isTrue={activeFiltersCount > 0}>
                        (
                        {activeFiltersCount}
                        )
                    </RenderIf>
                </Title>
                <div>
                    <Button
                        label="Clear"
                        variant="base"
                        size="small"
                        disabled={!activeFiltersCount}
                        borderRadius="semi-square"
                        onClick={clearFilters}
                    />
                    <ButtonIcon
                        borderRadius="semi-square"
                        size="small"
                        icon={<Close />}
                        onClick={onRequestClose}
                    />
                </div>
            </FilterHeader>
            <FiltersContent>
                <Accordion icon={<Calendar />} label="Created At" name="createdAt">
                    <StyledDatePickerFilter
                        onChange={handleChangeCreatedAt}
                        value={createdAtFilterValue}
                    />
                </Accordion>
                <Accordion icon={<Languages />} label="Preferred Language" name="preferredLanguage">
                    <StyledLanguageFilter
                        value={languageFilterValue}
                        onChange={handleChangeLanguageFilter}
                    />
                </Accordion>
                <Accordion icon={<TagIcon />} label="Tags" name="tags">
                    <StyledEntityTagsFilter
                        agentId={agentId}
                        value={tagsFilterValue}
                        onChange={handleChangeTagsFilter}
                        entity="customer"
                    />
                </Accordion>
            </FiltersContent>
            <Gradient position="bottom" />
        </FiltersContainer>
    );
};

export default Filters;
